import { PropsWithChildren } from 'react'
import type { NavigateFunction, Params } from 'react-router-dom'
import { useNavigate, useParams } from 'react-router-dom'

import { Box } from '@factorialco/deprecated-design-system'
import { Dropdown } from '@factorialco/f0-react/dist/experimental'
import { Features } from 'generated/featureDefinitions'
import { ExportTimeBalanceDocument } from 'generated/resources/graphql'
import moment from 'moment'

import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'
import { Navigation, NavigationPaths } from 'modules/core/lib/navigation/context'

import TimeTrackingDatePicker from 'modules/attendance/components/ClockIn/AdminLayout/WeeklyView/TimeTrackingDatePicker'
import { Cycle } from 'modules/attendance/lib/attendanceDateNavigatorContext'
import { useAttendanceFeatures } from 'modules/attendance/lib/attendanceFeaturesContext'
import { useCustomPeriods } from 'modules/attendance/store/collections/customPeriods'
import { ResponsivePage } from 'modules/layout/components/ResponsivePage'

import type CustomPeriodForm from './form'

type RouteParams = Readonly<Params<string>>

const onExportsModals =
  (params: RouteParams, navigate: NavigateFunction, navigation: Navigation<NavigationPaths>) =>
  () => {
    // Although the exclude process doesn't depend on a year and month, we
    // nest them in the same section to avoid an empty background.
    navigate(
      navigation.timeTracking.list.periodTypeYearMonthDay.exports.fullPath({
        periodType: params.periodType ?? '',
        year: Number(params.year),
        month: Number(params.month),
        day: Number(params.day),
      })
    )
  }

const onExportYearlyBalanceModals =
  (params: RouteParams, navigate: NavigateFunction, navigation: Navigation<NavigationPaths>) =>
  () => {
    track(trackingEvents.shiftManagement.requestYearlyExportStart, {
      export_from: 'time_tracking',
    }),
      // Although the export process doesn't depend on a year and month, we
      // nest them in the same section to avoid an empty background.
      navigate(
        navigation.timeTracking.list.periodTypeYearMonthDay.exportYearlyBalance.fullPath({
          periodType: params.periodType ?? '',
          year: Number(params.year),
          month: Number(params.month),
          day: Number(params.day),
        })
      )
  }

const onExcludeModals =
  (params: RouteParams, navigate: NavigateFunction, navigation: Navigation<NavigationPaths>) =>
  () => {
    // Although the exclude process doesn't depend on a year and month, we
    // nest them in the same section to avoid an empty background.
    navigate(
      navigation.timeTracking.list.periodTypeYearMonthDay.exclude.fullPath({
        periodType: params.periodType ?? '',
        year: Number(params.year),
        month: Number(params.month),
        day: Number(params.day),
      })
    )
  }

const getOptions = ({
  params,
  canEditTT,
  navigate,
  navigation,
  showExportYearlyBalanceOption,
}: {
  params: RouteParams
  canEditTT: boolean
  navigate: NavigateFunction
  navigation: Navigation<NavigationPaths>
  showExportYearlyBalanceOption: boolean
}) => {
  const options = [
    {
      onClick: onExportsModals(params, navigate, navigation),
      title: i18n.t('time_tracking.menu.download_title'),
      description: i18n.t('time_tracking.menu.download_description'),
    },
  ]

  if (showExportYearlyBalanceOption) {
    options.push({
      onClick: onExportYearlyBalanceModals(params, navigate, navigation),
      title: i18n.t('time_tracking.menu.download_annual_balance.title'),
      description: i18n.t('time_tracking.menu.download_annual_balance.description'),
    })
  }

  if (canEditTT) {
    options.push({
      onClick: onExcludeModals(params, navigate, navigation),
      title: i18n.t('time_tracking.menu.title'),
      description: i18n.t('time_tracking.menu.description'),
    })
  }

  return options
}

interface Props {
  customPeriodForm: CustomPeriodForm
  canAccessEditSettings: boolean
}

const Layout = ({
  customPeriodForm,
  canAccessEditSettings,
  children,
}: PropsWithChildren<Props>) => {
  const params = useParams()
  const periods = useCustomPeriods()
  const { navigation } = useNavigation()
  const navigate = useNavigate()
  const { featureAvailability: timeBalanceEnabled } = useAttendanceFeatures(
    Features.time_balance_annualisation_export
  )
  const { data } = useQuery(ExportTimeBalanceDocument, {})
  const canAccessTimeBalanceExport = data?.timeBalance?._policies.accessSeeTimeBalanceExport.can
  const showExportYearlyBalanceOption = timeBalanceEnabled && !!canAccessTimeBalanceExport

  const navigateToCustomPeriod = (startOn: Date, endOn: Date, periodType: Cycle) => {
    periods.set([])
    customPeriodForm.changePeriod(moment(startOn), moment(endOn), periodType)
    return navigate(
      navigation.timeTracking.list.periodTypeYearMonthDay.fullPath({
        periodType,
        year: startOn.getFullYear(),
        month: startOn.getMonth() + 1,
        day: startOn.getDate(),
      })
    )
  }

  const moduleActions = getOptions({
    params: params,
    canEditTT: canAccessEditSettings,
    navigate,
    navigation,
    showExportYearlyBalanceOption,
  })

  return (
    <ResponsivePage.Body showHeader={false}>
      <Box width="full" gap="s32">
        <Box
          flexDirection="row"
          justifyContent="flexEnd"
          alignItems="center"
          gap="s12"
          paddingBottom="s24"
        >
          <TimeTrackingDatePicker
            regularPeriods={['day', 'week', 'fortnight', 'month']}
            onChange={navigateToCustomPeriod}
          />
          {moduleActions.length > 0 && (
            <Dropdown
              items={moduleActions.map((button) => ({
                label: button.title,
                description: button.description,
                onClick: button.onClick,
              }))}
            />
          )}
        </Box>
      </Box>
      <Box width="full">{children}</Box>
    </ResponsivePage.Body>
  )
}

export default Layout
